import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { examService, userService, classService, studentService } from '../../services';
import { useAppDispatch } from '../../hooks';
import { useSelector } from 'react-redux';
import { notificationActions } from '../../redux/actions';
import { getImageUrl } from '../../utils/imageHelper';
import { RootState } from '../../store';

// Normalize True/False values to single-letter tokens for consistent comparison
const normalizeTF = (val: any) => {
  const v = String(val ?? '').trim().toLowerCase();
  if (v === 't' || v === 'true') return 'T';
  if (v === 'f' || v === 'false') return 'F';
  return v ? v[0].toUpperCase() : '';
};

const formatTFLabel = (norm: string) => {
  if (norm === 'T') return 'True';
  if (norm === 'F') return 'False';
  return norm || '—';
};

const parseTFAnswerParts = (answer: any, expectedLength?: number) => {
  const parts = Array.isArray(answer)
    ? answer
    : String(answer ?? '')
        .split(' | ')
        .map((p: string) => p.trim())
        .filter((p) => p.length > 0);
  const normalized = parts.map((p) => normalizeTF(p));
  if (expectedLength && normalized.length < expectedLength) {
    return [...normalized, ...Array(expectedLength - normalized.length).fill('')];
  }
  return normalized;
};

const SectionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 20px;
  font-size: 24px;
  color: #0f172a;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const SubmissionsTable = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;

    thead tr {
      background: #f8fafc;
      border-bottom: 2px solid #e2e8f0;
    }

    th {
      padding: 12px 16px;
      text-align: left;
      font-weight: 700;
      color: #0f172a;
      font-size: 13px;
      text-transform: uppercase;

      @media (max-width: 768px) {
        padding: 10px 12px;
        font-size: 12px;
      }
    }

    td {
      padding: 12px 16px;
      border-bottom: 1px solid #e2e8f0;
      color: #475569;

      @media (max-width: 768px) {
        padding: 10px 12px;
        font-size: 13px;
      }
    }

    tbody tr {
      transition: background 0.2s ease;
      cursor: pointer;

      &:hover {
        background: #f8fafc;
      }
    }
  }
`;

const ScrollableArea = styled.div`
  max-height: 600px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;

  /* Smooth custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const ScoreBadge = styled.span<{score: number}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 16px;
  background: ${(p) => {
    if (p.score >= 80) return '#d1fae5';
    if (p.score >= 60) return '#fef3c7';
    if (p.score >= 40) return '#fed7aa';
    return '#fee2e2';
  }};
  color: ${(p) => {
    if (p.score >= 80) return '#065f46';
    if (p.score >= 60) return '#92400e';
    if (p.score >= 40) return '#92400e';
    return '#991b1b';
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;

  .icon {
    font-size: 64px;
    margin-bottom: 16px;
  }

  .text {
    margin: 0;
    font-size: 16px;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #64748b;

  &::after {
    content: '';
    display: inline-block;
    width: 30px;
    height: 30px;
    border: 4px solid #e2e8f0;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalBox = styled.div`
  width: min(900px, 95vw);
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 20px;
    color: #0f172a;
    font-weight: 700;
  }
`;

const CloseButton = styled.button`
  padding: 8px 12px;
  background: #e2e8f0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: #cbd5e1;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const DetailCard = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  border: 1px solid #e2e8f0;

  .label {
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .value {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }
`;

const AnswersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AnswerItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  border-left: 4px solid #3b82f6;

  .question {
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 8px;
  }

  .answer {
    font-size: 13px;
    color: #475569;
    margin-bottom: 4px;

    strong {
      display: inline-block;
      min-width: 80px;
      color: #0f172a;
    }
  }

  .result {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    margin-top: 8px;

    &.correct {
      background: #d1fae5;
      color: #065f46;
    }

    &.incorrect {
      background: #fee2e2;
      color: #991b1b;
    }
  }
`;

const RankingLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  animation: fadeIn 0.4s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LoadingSpinnerCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  &::before {
    content: '';
    display: inline-block;
    width: 40px;
    height: 40px;
    border: 4px solid #e0e7ff;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .loading-text {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const RankingItem = styled.div<{ $index: number }>`
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  cursor: pointer;
  animation: fadeInSlide 0.5s ease-out;
  animation-delay: ${props => props.$index * 0.05}s;
  animation-fill-mode: both;

  @keyframes fadeInSlide {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
`;

interface CompletedExamsSectionProps {
  userId?: number;
  onExamsLoaded?: (exams: any[]) => void;
}

const CompletedExamsSection: React.FC<CompletedExamsSectionProps> = ({ userId, onExamsLoaded }) => {
  const dispatch = useAppDispatch();
  const { items: users = [] } = useSelector((state: RootState) => (state as any).users) || {};
  const classes = useSelector((state: RootState) => (state as any).classes?.classesList || []);
  const allStudentsData = useSelector((state: RootState) => (state as any).students?.studentsList || []);
  
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<any | null>(null);
  const [selectedExamData, setSelectedExamData] = useState<any | null>(null);
  const [showExplanationMap, setShowExplanationMap] = useState<Record<number, boolean>>({});
  const [detailTab, setDetailTab] = useState<'detail' | 'ranking'>('detail');
  const [rankingData, setRankingData] = useState<any[]>([]);
  const [isLoadingRanking, setIsLoadingRanking] = useState(false);

  console.log('CompletedExamsSection render - userId:', userId);

  useEffect(() => {
    if (userId) {
      console.log('CompletedExamsSection useEffect - loading submissions for userId:', userId);
      loadSubmissions();
    }
  }, [userId]);

  const loadSubmissions = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await examService.getStudentSubmissions(userId);
      console.log('Submissions response:', response);
      const data = response.data || response;
      const exams = Array.isArray(data) ? data : [];
      setSubmissions(exams);
      // Notify parent about loaded exams
      if (onExamsLoaded) {
        onExamsLoaded(exams);
      }
    } catch (error: any) {
      console.error('Error loading submissions:', error);
      dispatch(notificationActions.addNotification('Lỗi khi tải bài làm', 'DANGER'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateTimeTaken = (submission: any) => {
    if (!submission.started_at || !submission.finished_at) return '—';
    const start = new Date(submission.started_at).getTime();
    const end = new Date(submission.finished_at).getTime();
    const diffMs = end - start;
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes} phút ${seconds} giây`;
  };

  // Function to get student name, class, and avatar for a submission
  const getSubmissionNameAndClass = (submission: any, studentMap: any = {}) => {
    const userId = submission?.student_id;
    
    // Default fallback
    let name = submission?.participant_name || 'Ẩn danh';
    let className = 'Tự Do';
    let avatar = null;
    
    if (userId && studentMap[userId]) {
      const studentData = studentMap[userId];
      name = studentData.name || submission?.participant_name || 'Ẩn danh';
      className = studentData.className || 'Tự Do';
      avatar = studentData.avatar || null;
    }
    
    return { name, className, avatar };
  };

  const generateRankingData = (allSubmissions: any[], studentMap: any = {}) => {
    // Group by student_id and get best submission per student
    const studentBestSubmissions: Record<number, any> = {};
    allSubmissions.forEach(submission => {
      const studentId = submission.student_id || 0;
      if (!studentBestSubmissions[studentId] || 
          submission.total_score > studentBestSubmissions[studentId].total_score) {
        studentBestSubmissions[studentId] = submission;
      }
    });
    
    // Convert to array and sort by score (desc) then by time_taken (asc)
    const ranked = Object.values(studentBestSubmissions)
      .sort((a, b) => {
        if (b.total_score !== a.total_score) {
          return b.total_score - a.total_score;
        }
        // If same score: compare time taken (finished_at - started_at), not finish timestamp
        const timeA = new Date(a.finished_at).getTime() - new Date(a.started_at).getTime();
        const timeB = new Date(b.finished_at).getTime() - new Date(b.started_at).getTime();
        return timeA - timeB;
      })
      .map((item, idx) => {
        const nameAndClass = getSubmissionNameAndClass(item, studentMap);
        return {
          ...item,
          rank: idx + 1,
          studentName: nameAndClass.name,
          studentClassName: nameAndClass.className,
          studentAvatar: nameAndClass.avatar,
        };
      });
    
    return ranked;
  };

  const handleOpenRanking = async () => {
    const examId = selectedSubmission?.exam_id || selectedSubmission?.exam?.id;
    console.log('handleOpenRanking - examId:', examId);
    
    if (examId) {
      setIsLoadingRanking(true);
      try {
        // Fetch submissions for this exam
        const submissionsRes = await examService.getSubmissions(examId);
        const allSubmissions = Array.isArray(submissionsRes) ? submissionsRes : (submissionsRes?.data || []);
        
        console.log('submissions count:', allSubmissions.length);
        
        // Fetch students with class names from the new endpoint
        const studentsWithClassRes = await studentService.getStudentsWithClassNames();
        const studentsWithClass = Array.isArray(studentsWithClassRes) ? studentsWithClassRes : (studentsWithClassRes?.data || studentsWithClassRes || []);
        
        console.log('=== STUDENTS WITH CLASS NAMES ===');
        console.log('studentsWithClass count:', studentsWithClass.length);
        console.log('first student:', studentsWithClass[0]);
        
        // Create a map from user ID to student data
        // We need to find user->student mapping
        const fetchedUsers = await userService.getAll();
        const usersArray = Array.isArray(fetchedUsers) ? fetchedUsers : (fetchedUsers?.data || []);
        
        console.log('users count:', usersArray.length);
        
        // Create mapping: userId -> {name, className, avatar}
        const studentMap: Record<number, any> = {};
        usersArray.forEach((user: any) => {
          if (user.studentId) {
            const studentData = studentsWithClass.find((s: any) => s.id === user.studentId);
            if (studentData) {
              studentMap[user.id] = {
                name: studentData.name,
                className: studentData.className,
                avatar: user.avatar // Get avatar from users table
              };
            }
          }
        });
        
        console.log('studentMap created with', Object.keys(studentMap).length, 'entries');
        
        const ranked = generateRankingData(allSubmissions, studentMap);
        console.log('Generated ranking data:', ranked);
        setRankingData(ranked);
        setDetailTab('ranking');
      } catch (err) {
        console.error('Error fetching exam submissions for ranking:', err);
        dispatch(notificationActions.addNotification('Lỗi khi tải bảng xếp hạng', 'DANGER'));
      } finally {
        setIsLoadingRanking(false);
      }
    }
  };

  const getCorrectAnswer = (question: any): string => {
    if (!question) return '—';

    const typeId = question.question_type_id || 1;
    let content = question.content || {};
    if (typeof content === 'string') {
      try {
        content = JSON.parse(content);
      } catch {
        content = {};
      }
    }

    // Type 1: MCQ - show option letter and text
    if (typeId === 1 && content.options && content.correct_index !== undefined) {
      const label = String.fromCharCode(65 + content.correct_index);
      return `${label}. ${content.options[content.correct_index]}`;
    }

    // Type 2: Verb Conjugation - correct_answer
    if (typeId === 2 && content.correct_answer) {
      return content.correct_answer;
    }

    // Type 3: Sentence Ordering - correct_answers array or correct_sentence (backward compatibility)
    if (typeId === 3) {
      if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
        return content.correct_answers.join(' | ');
      }
      if (content.correct_sentence) {
        return content.correct_sentence;
      }
    }

    // Type 4: Write Sentence - join prompts answers with ' | '
    if (typeId === 4 && content.prompts && Array.isArray(content.prompts)) {
      const allAnswers: string[] = [];
      for (const p of content.prompts) {
        if (p.answers && Array.isArray(p.answers)) {
          // New format: multiple answers per prompt
          allAnswers.push(p.answers.filter((a: string) => a?.trim()).join(' / '));
        } else if (p.answer) {
          // Old format: single answer
          allAnswers.push(p.answer);
        }
      }
      return allAnswers.filter(a => a).join(' | ');
    }

    // Type 5: Rewrite Sentence - correct_answers array or correct_answer (backward compatibility)
    if (typeId === 5) {
      if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
        return content.correct_answers.join(' | ');
      }
      if (content.correct_answer) {
        return content.correct_answer;
      }
    }

    // Type 6: Reading Essay - show all correct_answers from sub-questions
    if (typeId === 6 && content.questions && Array.isArray(content.questions)) {
      return content.questions
        .map((q: any, i: number) => {
          const answers = Array.isArray(q.correct_answers) 
            ? q.correct_answers.filter((a: string) => a?.trim()).join(' / ')
            : '—';
          return `${i + 1}) ${answers}`;
        })
        .join(' | ');
    }

    // Type 7: Reading MCQ - show all correct options from sub-questions
    if (typeId === 7 && content.questions && Array.isArray(content.questions)) {
      return content.questions
        .map((q: any, i: number) => {
          if (q.options && q.correct_index !== undefined) {
            const label = String.fromCharCode(65 + q.correct_index);
            return `${i + 1}) ${label}`;
          }
          return `${i + 1}) —`;
        })
        .join(' | ');
    }

    // Type 8: Fill Blanks Word Bank - answers array
    if (typeId === 8 && content.answers && Array.isArray(content.answers)) {
      return content.answers.join(', ');
    }

    // Type 9: Fill Blanks MCQ - show correct option with full text
    if (typeId === 9 && content.questions && Array.isArray(content.questions)) {
      return content.questions
        .map((q: any, i: number) => {
          if (q.options && q.correct_index !== undefined) {
            const label = String.fromCharCode(65 + q.correct_index);
            const text = q.options[q.correct_index];
            return `${i + 1}) ${label}. ${text}`;
          }
          return `${i + 1}) —`;
        })
        .join(' | ');
    }

    // Type 10: Find Error - prefer MCQ error identification payload shape
    if (typeId === 10) {
      if (Array.isArray(content.options) && content.wrong_index !== undefined) {
        const wrongIdx = Number(content.wrong_index);
        if (!Number.isNaN(wrongIdx) && content.options[wrongIdx] !== undefined) {
          const label = String.fromCharCode(65 + wrongIdx);
          const correction = content.correct_answer || content.correction || '—';
          return `${label}. ${content.options[wrongIdx]} → ${correction}`;
        }
      }
      const error = content.error_word || '—';
      const correction = content.correction || '—';
      return `${error} → ${correction}`;
    }

    // Type 11: Make Question - correct_questions array or correct_question (backward compatibility)
    if (typeId === 11) {
      if (content.correct_questions && Array.isArray(content.correct_questions) && content.correct_questions.length > 0) {
        return content.correct_questions.join(' | ');
      }
      if (content.correct_question) {
        return content.correct_question;
      }
      if (question.correctAnswers && question.correctAnswers.length > 0) {
        return question.correctAnswers.map((ca: any) => ca.answer).join(' | ');
      }
      return '—';
    }

    // Type 12: True/False - show T/F for each statement
    if (typeId === 12 && content.statements && Array.isArray(content.statements)) {
      return content.statements
        .map((stmt: any, i: number) => `${i + 1}) ${stmt.is_true ? 'T' : 'F'}`)
        .join(' | ');
    }

    // Type 13: Word Form - return correct_answer from content
    if (typeId === 13 && content.correct_answer) {
      return content.correct_answer;
    }

    // Use correctAnswers table if available
    if (question.correctAnswers && question.correctAnswers.length > 0) {
      return question.correctAnswers.map((ca: any) => ca.answer).join(' | ');
    }

    // Fallback
    return question.correct_answer || '—';
  };

  const shouldHighlightWordDiff = (typeId?: number) => [2, 3, 4, 5, 11, 13].includes(Number(typeId));

  const splitCorrectAnswerCandidates = (answerText: string) =>
    answerText
      .split('|')
      .map((a) => a.trim())
      .filter((a) => a && a !== '—');

  const highlightMismatchedWords = (studentText: string, correctCandidates: string[]): React.ReactNode => {
    if (!studentText || studentText === '(Trống)' || studentText === '—' || correctCandidates.length === 0) return studentText;

    const normalizeToken = (token: string) => token.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').toLowerCase();

    const tokenizeWithSpaces = (text: string) =>
      text
        .split(/(\s+)/)
        .filter((part) => part !== '')
        .map((part) => ({
          raw: part,
          isSpace: part.trim() === '',
          norm: part.trim() === '' ? '' : normalizeToken(part),
        }));

    const buildFreqMap = (tokens: { norm: string; isSpace: boolean }[]) =>
      tokens.reduce<Record<string, number>>((acc, t) => {
        if (!t.isSpace && t.norm) {
          acc[t.norm] = (acc[t.norm] || 0) + 1;
        }
        return acc;
      }, {});

    const scoreCandidate = (studentTokens: { norm: string; isSpace: boolean }[], candidateTokens: { norm: string; isSpace: boolean }[]) => {
      const freq = buildFreqMap(candidateTokens);
      let matches = 0;
      studentTokens.forEach((t) => {
        if (!t.isSpace && t.norm && freq[t.norm]) {
          matches += 1;
          freq[t.norm] -= 1;
        }
      });
      return matches;
    };

    const studentTokens = tokenizeWithSpaces(studentText);
    const candidatesTokens = correctCandidates.map(tokenizeWithSpaces);
    const bestCandidateIndex = candidatesTokens.reduce((bestIdx, tokens, idx) => {
      const bestScore = scoreCandidate(studentTokens, candidatesTokens[bestIdx] || []);
      const score = scoreCandidate(studentTokens, tokens);
      return score > bestScore ? idx : bestIdx;
    }, 0);

    const bestTokens = candidatesTokens[bestCandidateIndex] || [];
    const freq = buildFreqMap(bestTokens);

    return (
      <span>
        {studentTokens.map((t, idx) => {
          if (t.isSpace || !t.norm) {
            return <React.Fragment key={idx}>{t.raw}</React.Fragment>;
          }
          const isMatch = freq[t.norm] > 0;
          if (isMatch) {
            freq[t.norm] -= 1;
            return <React.Fragment key={idx}>{t.raw}</React.Fragment>;
          }
          return (
            <span key={idx} style={{ color: '#dc2626', fontWeight: 600 }}>
              {t.raw}
            </span>
          );
        })}
      </span>
    );
  };

  const renderAnswerDetail = (answer: any, index: number) => {
    const question = answer.question;
    let content: any = question?.content || {};
    if (typeof content === 'string') {
      try {
        content = JSON.parse(content);
      } catch {
        content = {};
      }
    }
    const typeId = question?.question_type_id;
    
    // Format question text based on type
    const questionText = (() => {
      if (typeId === 6 || typeId === 7 || typeId === 9) {
        if (content.passage) {
          const blankRegex = /_{3,}|\.{3,}/g;
          let blankIndex = 1;
          return content.passage.replace(blankRegex, () => `(${blankIndex++}) ____`);
        }
      }
      if (typeId === 13 && content.sentence) return content.sentence;
      if (typeId === 10 && content.sentence) return content.sentence;
      if (typeId === 11 && content.sentence) {
        const sentence = content.sentence;
        const underlined = content.underlined || '';
        if (underlined && sentence.includes(underlined)) {
          const parts = sentence.split(underlined);
          return (<>{parts[0]}<u style={{ fontWeight: 'bold' }}>{underlined}</u>{parts[1] || ''}</> as any);
        }
        return sentence;
      }
      if (typeId === 12) {
        const passage = content.passage ? content.passage : '';
        if (Array.isArray(content.statements) && content.statements.length > 0) {
          const stmtText = content.statements.map((s: any, i: number) => `${i + 1}) ${s.text || ''}`).join(' | ');
          return (<>{passage && <div style={{ marginBottom: 6 }}>{content.passage}</div>}<div>{stmtText}</div></> as any);
        }
        return passage || '(Không có nội dung)';
      }
      if (typeId === 3 && Array.isArray(content.words)) {
        return (content.words || []).join(' / ');
      }
      return content.text || content.original_sentence || content.sentence || '(Không có nội dung)';
    })();
    
    // Format student answer
    const studentAnswer = (() => {
      if (typeId === 13) {
        // For word form questions, trim and normalize whitespace
        return (answer.answer || '').trim();
      }
      if (typeId === 9 && content?.questions && Array.isArray(content.questions)) {
        const answerParts = answer.answer ? answer.answer.split(' | ').map((s: string) => s.trim()) : [];
        return answerParts.map((ans: string, i: number) => {
          const subQ = content.questions[i];
          if (subQ?.options) {
            const optIdx = subQ.options.findIndex((opt: string) => opt === ans);
            if (optIdx !== -1) {
              const label = String.fromCharCode(65 + optIdx);
              return `${i + 1}) ${label}. ${ans}`;
            }
          }
          return `${i + 1}) ${ans}`;
        }).join(' | ');
      }
      if (typeId === 10 && Array.isArray(content.options)) {
        const userIdx = Number(answer.answer);
        if (!Number.isNaN(userIdx) && content.options[userIdx] !== undefined) {
          const label = String.fromCharCode(65 + userIdx);
          return `${label}. ${content.options[userIdx]}`;
        }
      }
      if (typeId === 12 && Array.isArray(content.statements)) {
        const answerParts = parseTFAnswerParts(answer.answer, content.statements.length);
        return content.statements.map((_: any, i: number) => `${i + 1}) ${formatTFLabel(answerParts[i] || '')}`).join(' | ');
      }
      return answer.answer || '(Trống)';
    })();

    // Check if answer is correct (for typeId=13, compare with correct_answer)
    const isAnswerCorrect = (() => {
      if (typeId === 13 && content.correct_answer) {
        // Case-insensitive comparison for word form
        return (answer.answer || '').trim().toLowerCase() === content.correct_answer.trim().toLowerCase();
      }
      return answer.is_correct;
    })();

    const correctAnswerText = getCorrectAnswer(question);
    const correctCandidates = shouldHighlightWordDiff(typeId) ? splitCorrectAnswerCandidates(correctAnswerText) : [];
    const highlightedStudentAnswer =
      !isAnswerCorrect && shouldHighlightWordDiff(typeId) && typeof studentAnswer === 'string' && correctCandidates.length > 0
        ? highlightMismatchedWords(studentAnswer, correctCandidates)
        : studentAnswer;
    const acceptedLabel = correctCandidates.length > 1 ? 'Đáp án đúng được chấp nhận:' : 'Đáp án đúng:';
    const renderAcceptedAnswers = () => {
      if (correctCandidates.length > 1) {
        return (
          <div style={{ display: 'inline-block' }}>
            {correctCandidates.map((ans, idx) => (
              <div key={idx}>{ans}</div>
            ))}
          </div>
        );
      }
      return correctAnswerText;
    };

    const explanation = question?.explanation;
    const explanationOpen = question?.id ? showExplanationMap[question.id] : false;
    const toggleExplanation = () => {
      if (!question?.id) return;
      setShowExplanationMap(prev => ({ ...prev, [question.id]: !prev[question.id] }));
    };
    
    return (
      <AnswerItem key={answer.id || index}>
        <div className="question">
          {index}. {typeId === 12 && Array.isArray(content.statements) ? 'True / False' : questionText}
          <span style={{ fontStyle: 'italic', fontSize: 12, color: '#999', marginLeft: '6px' }}>
            [{question?.id || 'N/A'}]
          </span>
        </div>
        {typeId === 12 && content?.passage && (
          <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px', fontStyle: 'italic' }}>
            {content.passage}
          </div>
        )}
        {typeId === 12 && Array.isArray(content.statements) && (
          <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px' }}>
            {content.statements.map((stmt: any, i: number) => (
              <div key={i}>{i + 1}) {stmt.text}</div>
            ))}
          </div>
        )}
        <div className="answer">
          <strong>Câu trả lời:</strong> {highlightedStudentAnswer}
        </div>
        <div className="answer" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <strong>{acceptedLabel}</strong>
          <div>{renderAcceptedAnswers()}</div>
        </div>
        {explanation && (
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={toggleExplanation}
              style={{
                background: '#e0f2fe',
                border: '1px solid #93c5fd',
                color: '#0f172a',
                padding: '4px 10px',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {explanationOpen ? 'Ẩn giải thích' : 'Hiện giải thích'}
            </button>
            {explanationOpen && (
              <div style={{ marginTop: 6, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                {explanation}
              </div>
            )}
          </div>
        )}
        {answer.total_sub_questions && answer.total_sub_questions > 1 ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {(answer.correct_count || 0) > 0 && (
              <span className="result correct">
                ✓ Đúng({answer.correct_count})
              </span>
            )}
            {(answer.total_sub_questions - (answer.correct_count || 0)) > 0 && (
              <span className="result incorrect">
                ✗ Sai({answer.total_sub_questions - (answer.correct_count || 0)})
              </span>
            )}
          </div>
        ) : (
          <span className={`result ${isAnswerCorrect ? 'correct' : 'incorrect'}`}>
            {isAnswerCorrect ? '✓ Đúng' : '✗ Sai'}
          </span>
        )}
        {answer.score !== undefined && (
          <div className="answer" style={{ marginTop: 8 }}>
            <strong>Điểm:</strong> {answer.score}
          </div>
        )}
      </AnswerItem>
    );
  };

  if (loading) {
    return (
      <SectionCard>
        <SectionTitle>📊 Đề Thi Đã Hoàn Thành</SectionTitle>
        <LoadingSpinner />
      </SectionCard>
    );
  }

  return (
    <>
      <SectionCard>
        <SectionTitle>📊 Đề Thi Đã Hoàn Thành ({submissions.length})</SectionTitle>

        {submissions.length === 0 ? (
          <EmptyState>
            <div className="icon">📈</div>
            <p className="text">Bạn chưa hoàn thành đề thi nào</p>
          </EmptyState>
        ) : (
          <ScrollableArea>
            <SubmissionsTable>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Tên đề thi</th>
                    <th>Điểm</th>
                    <th>Thời gian làm</th>
                    <th>Ngày nộp</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission, index) => (
                    <tr
                      key={submission.id}
                      onClick={async () => {
                        setSelectedSubmission(submission);
                        setSelectedExamData(null);
                        if (submission.exam_id || submission.exam?.id) {
                          try {
                            const examId = submission.exam_id || submission.exam?.id;
                            console.log('Fetching exam data with exam_id:', examId);
                            const examData = await examService.getById(examId);
                            console.log('Fetched exam data raw:', examData);
                            const actualData = examData.data || examData;
                            console.log('Actual exam data to set:', actualData);
                            setSelectedExamData(actualData);
                          } catch (err) {
                            console.error('Error fetching exam data:', err);
                            setSelectedExamData(null);
                          }
                        } else {
                          console.log('No exam_id found on submission');
                        }
                      }}
                    >
                      <td>{index + 1}</td>
                      <td>
                        {submission.submission_type === 'retry' 
                          ? 'Luyện tập câu sai'
                          : submission.exam?.title || submission.participant_name || '—'
                        }
                      </td>
                    <td>
                      <ScoreBadge score={submission.total_score || 0}>
                        {submission.total_score || 0}
                      </ScoreBadge>
                    </td>
                    <td>{calculateTimeTaken(submission)}</td>
                    <td>{formatDate(submission.finished_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </SubmissionsTable>
          </ScrollableArea>
        )}
      </SectionCard>

      {selectedSubmission && (
        <ModalOverlay onClick={() => setSelectedSubmission(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: '-1px' }}>
                  <button
                    onClick={() => setDetailTab('detail')}
                    style={{
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: detailTab === 'detail' ? '700' : '500',
                      background: 'none',
                      border: 'none',
                      borderBottom: detailTab === 'detail' ? '2px solid #2563eb' : 'none',
                      color: detailTab === 'detail' ? '#2563eb' : '#666',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      marginBottom: '-2px',
                    }}
                  >
                    📋 Chi tiết
                  </button>
                  <button
                    onClick={handleOpenRanking}
                    style={{
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: detailTab === 'ranking' ? '700' : '500',
                      background: 'none',
                      border: 'none',
                      borderBottom: detailTab === 'ranking' ? '2px solid #2563eb' : 'none',
                      color: detailTab === 'ranking' ? '#2563eb' : '#666',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      marginBottom: '-2px',
                    }}
                  >
                    🏆 Xếp hạng
                  </button>
                </div>
              </div>
              <CloseButton onClick={() => {
                setSelectedSubmission(null);
                setDetailTab('detail');
              }}>Đóng</CloseButton>
            </ModalHeader>

            <ModalContent>
              {detailTab === 'detail' ? (
                <>
                  <DetailGrid>
                    <DetailCard>
                      <div className="label">Điểm số</div>
                      <div className="value" style={{ fontSize: 24 }}>
                        {selectedSubmission.total_score || 0}/{selectedSubmission.exam?.total_marks || 100}
                      </div>
                    </DetailCard>
                    <DetailCard>
                      <div className="label">Phần trăm</div>
                      <div className="value">
                        {selectedSubmission.exam?.total_marks 
                          ? Math.round((selectedSubmission.total_score / selectedSubmission.exam.total_marks) * 100)
                          : 0}%
                      </div>
                    </DetailCard>
                    <DetailCard>
                      <div className="label">Thời gian làm</div>
                      <div className="value">{calculateTimeTaken(selectedSubmission)}</div>
                    </DetailCard>
                    <DetailCard>
                      <div className="label">Ngày nộp</div>
                      <div className="value" style={{ fontSize: 13 }}>
                        {formatDate(selectedSubmission.finished_at)}
                      </div>
                    </DetailCard>
                  </DetailGrid>

                  {selectedSubmission.user_answers && selectedSubmission.user_answers.length > 0 && (
                    <>
                      <h4 style={{ margin: '16px 0 12px', color: '#0f172a', fontWeight: 700 }}>
                        Chi tiết các câu trả lời:
                      </h4>
                  <AnswersList>
                    {selectedExamData?.exam_questions ? (() => {
                      console.log('Grouping exam_questions:', selectedExamData.exam_questions);
                      // Group by exam question types
                      const groups: Record<number, any[]> = {};
                      selectedExamData.exam_questions.forEach((eq: any) => {
                        const typeId = eq.question?.question_type_id || 0;
                        console.log('Processing exam question:', eq, 'typeId:', typeId);
                        if (!groups[typeId]) groups[typeId] = [];
                        groups[typeId].push(eq);
                      });

                      const sortedTypes = Object.keys(groups).map(Number).sort((a, b) => a - b);
                      console.log('Sorted types:', sortedTypes);
                      const userAnswerMap = new Map();
                      selectedSubmission.user_answers.forEach((ua: any) => {
                        userAnswerMap.set(Number(ua.question?.id || ua.question_id), ua);
                      });
                      console.log('User answer map:', userAnswerMap);

                      return sortedTypes.map((typeId, exerciseIdx) => {
                        const typeInfo = selectedExamData.exam_questions.find((eq: any) => eq.question?.question_type_id === typeId)?.question?.questionType;
                        const engTitle = typeInfo?.eng_title || '';
                        const vnTitle = typeInfo?.vn_title || '';
                        const headerText = `Exercise ${exerciseIdx + 1}: ${engTitle}${vnTitle ? ` (${vnTitle})` : ''}`;
                        
                        let questionNum = 0;
                        selectedExamData.exam_questions.forEach((eq: any) => {
                          if (eq.question?.question_type_id < typeId) questionNum++;
                        });
                        
                        return (
                          <div key={`exercise-${typeId}`} style={{ marginBottom: '16px' }}>
                            <h5 style={{
                              color: '#1d4ed8',
                              fontSize: '14px',
                              fontWeight: 700,
                              margin: '0 0 8px 0',
                              borderBottom: '2px solid #bfdbfe',
                              paddingBottom: '4px'
                            }}>
                              {headerText}
                            </h5>
                            {groups[typeId].map((eq: any, idx: number) => {
                              const answer = userAnswerMap.get(Number(eq.question?.id));
                              if (!answer) return null;
                              return renderAnswerDetail(answer, questionNum + idx + 1);
                            })}
                          </div>
                        );
                      });
                    })() : selectedSubmission.user_answers.map((answer: any, index: number) => renderAnswerDetail(answer, index + 1))}
                  </AnswersList>
                </>
              )}
                </>
              ) : (
                /* Ranking Tab */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {isLoadingRanking ? (
                    <RankingLoadingContainer>
                      <LoadingSpinnerCenter>
                        <div className="loading-text">📊 Đang tải bảng xếp hạng...</div>
                      </LoadingSpinnerCenter>
                    </RankingLoadingContainer>
                  ) : rankingData.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999', fontSize: '14px' }}>
                      Chưa có bài nộp để xếp hạng
                    </div>
                  ) : (
                    rankingData.map((item, index) => (
                      <RankingItem
                        key={item.id}
                        $index={index}
                        style={{
                          background: item.rank === 1 ? 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)' : 
                                     item.rank === 2 ? 'linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)' :
                                     item.rank === 3 ? 'linear-gradient(135deg, #cd7f32 0%, #e5b87a 100%)' :
                                     'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                        }}
                      >
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: item.rank === 1 ? '#ffc107' : item.rank === 2 ? '#c0c0c0' : item.rank === 3 ? '#cd7f32' : '#e0e0e0',
                            color: item.rank <= 3 ? 'white' : '#333',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px',
                            flexShrink: 0,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          }}
                        >
                          {item.rank}
                        </div>
                        
                        {item.studentAvatar ? (
                          <img
                            src={getImageUrl(item.studentAvatar)}
                            alt={item.studentName}
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              flexShrink: 0,
                              border: '2px solid white',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '700',
                              flexShrink: 0,
                              border: '2px solid white',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            }}
                          >
                            {(item.studentName || 'H')[0].toUpperCase()}
                          </div>
                        )}
                        
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                          <div style={{ fontWeight: '600', color: '#333', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {item.studentName || 'Ẩn danh'} ({item.studentClassName || 'Tự Do'})
                            {item.rank === 1 && <span style={{ fontSize: '18px' }}>🥇</span>}
                            {item.rank === 2 && <span style={{ fontSize: '18px' }}>🥈</span>}
                            {item.rank === 3 && <span style={{ fontSize: '18px' }}>🥉</span>}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'right' }}>
                            <div style={{ fontSize: '13px', color: '#000', fontWeight: '700' }}>
                              {item.total_score}
                              {item.exam?.total_marks ? `/${item.exam.total_marks}` : ''} điểm
                            </div>
                            <div style={{ fontSize: '12px', color: '#000', fontWeight: '700' }}>
                              Thời gian làm: {(() => {
                                if (!item.started_at || !item.finished_at) return '—';
                                const start = new Date(item.started_at).getTime();
                                const end = new Date(item.finished_at).getTime();
                                const diffMs = end - start;
                                const minutes = Math.floor(diffMs / 60000);
                                const seconds = Math.floor((diffMs % 60000) / 1000);
                                return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                              })()}
                            </div>
                          </div>
                        </div>
                      </RankingItem>
                    ))
                  )}
                </div>
              )}
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
};

export default CompletedExamsSection;
